import React from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Quick "find similar on portal" buttons, pre-filtered by city + sqm range.
 * Avoids any scraping/legal exposure — just deep-links to official portal search
 * with filters applied. Opens in new tab.
 *
 * Props:
 *   city: "Milano"
 *   surface_sqm: 95
 *   property_type: "appartamento" | "attico" | "villa" | ...
 */
export default function PortalCompare({ city, surface_sqm, property_type = "appartamento" }) {
  if (!city) return null;

  const sqm = surface_sqm ? Math.round(Number(surface_sqm)) : null;
  const sqmMin = sqm ? Math.max(20, Math.round(sqm * 0.8)) : null;
  const sqmMax = sqm ? Math.round(sqm * 1.2) : null;

  const slug = (s) =>
    (s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-");
  const citySlug = slug(city);

  // Idealista: https://www.idealista.it/vendita-case/milano/con-metriquadrati_80,120/
  const idealista = sqm
    ? `https://www.idealista.it/vendita-case/${citySlug}/con-metriquadrati_${sqmMin},${sqmMax}/`
    : `https://www.idealista.it/vendita-case/${citySlug}/`;

  // Immobiliare.it
  const immobiliareParams = new URLSearchParams({ criterio: "rilevanza", noAste: "1" });
  if (sqmMin) immobiliareParams.append("superficieMinima", String(sqmMin));
  if (sqmMax) immobiliareParams.append("superficieMassima", String(sqmMax));
  const immobiliare = `https://www.immobiliare.it/vendita-case/${citySlug}/?${immobiliareParams.toString()}`;

  const portals = [
    { label: "Idealista", href: idealista, testId: "portal-idealista", color: "#E01919" },
    { label: "Immobiliare.it", href: immobiliare, testId: "portal-immobiliare", color: "#E23744" },
  ];

  return (
    <div className="bg-[#FAFAF7] border border-[#E8E5DE] rounded-lg p-4" data-testid="portal-compare">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-semibold text-[#9A968B] mb-2">
        <MagnifyingGlass size={12} /> Confronta con case simili
      </div>
      <div className="text-[12px] text-[#6B6A65] leading-[1.5] mb-3">
        Apri ricerche filtrate per {sqm ? `${sqmMin}-${sqmMax} m²` : "questa tipologia"} a {city}.
        Guarda cosa c'è in vendita adesso nella zona per capire se il tuo prezzo regge il confronto.
      </div>
      <div className="flex flex-wrap gap-1.5">
        {portals.map((p) => (
          <a
            key={p.label}
            href={p.href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-white hover:bg-[#F5F4F0] border border-[#E8E5DE] hover:border-[#C8C5BD] rounded-md px-3 py-1.5 text-[12px] font-medium text-[#1C1C1A] transition-all"
            data-testid={p.testId}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: p.color }}></span>
            {p.label}
          </a>
        ))}
      </div>
    </div>
  );
}
